import React, { useEffect, useState, useRef } from "react";
import {
  AreaChart,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Area,
} from "recharts";
import L from "leaflet";
import { Switch, FormControlLabel } from "@mui/material";
import dayjs from "dayjs";
import { iconDotChartGray_cs, iconDotChartOKIT_cs } from "../../utils/iconPin";

const ChartAnalyticMap = ({
  eventForChart,
  nodeForChart,
  mapRef,
  setIsLoadingChart,
  isDotsChartActive,
  setIsDotsChartActive,
}) => {
  const [autoScale, setAutoScale] = useState(true);
  const markersRef = useRef({});
  const redMarkerRef = useRef(null); // Store reference to the red marker
  let minBoundaryValue, maxBoundaryValue;

  useEffect(() => {
    if (!eventForChart) return;

    if (isDotsChartActive) {
      mapRef.current.eachLayer((layer) => {
        if (layer instanceof L.Marker && layer.options.isDotChart) {
          mapRef.current.removeLayer(layer);
        }
      });
      nodeForChart !== null &&
        eventForChart.forEach((measurement) => {
          if (measurement?.latitude && measurement?.longitude) {
            addDots(measurement);
          }
        });
      setIsLoadingChart(false);
    } else {
      mapRef.current.eachLayer((layer) => {
        if (layer instanceof L.Marker && layer.options.isDotChart) {
          mapRef.current.removeLayer(layer);
        }
      });
      setIsLoadingChart(false);
    }
    //eslint-disable-next-line
  }, [eventForChart, autoScale, nodeForChart, isDotsChartActive]);

  const formattedData = eventForChart.map((d) => ({
    ...d,
    formattedTime: dayjs(d.measurementdate).format("DD.MM.YYYY HH:mm:ss"),
    content:
      d.contentType === "BOOL" ? (d.content === "true" ? 1 : 0) : d.content,
  }));

  const contentValues = formattedData.map((d) => d.content);
  const contentType = formattedData[0]?.contentType;
  if (contentType === "BOOL") {
    minBoundaryValue = 0;
    maxBoundaryValue = 1;
  } else if (contentType === "FLOAT" || contentType === "DOUBLE") {
    minBoundaryValue = Math.min(...contentValues);
    maxBoundaryValue = Math.max(...contentValues);
  }

  const addDots = (measurement) => {
    const marker = L.marker([measurement.latitude, measurement.longitude], {
      icon: iconDotChartOKIT_cs,
      isDotChart: true,
    });

    const nodeParts = measurement.node.split(".");
    const lastWord = nodeParts[nodeParts.length - 1];
    const formattedDate = formatMeasurementDate(measurement.measurementdate);
    const msg = `${lastWord} : ${measurement.content} <br/> Time : ${formattedDate}`;

    marker.bindPopup(msg);
    marker.addTo(mapRef.current);

    // Store marker reference for hover effect
    markersRef.current[measurement.measurementdate] = marker;
  };

  function formatMeasurementDate(measurementDate) {
    const date = new Date(measurementDate);
    return `${date.getHours().toString().padStart(2, "0")}:${date
      .getMinutes()
      .toString()
      .padStart(2, "0")} ${date.getDate().toString().padStart(2, "0")}.${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}.${date.getFullYear()}`;
  }

  const handleAutoScale = () => {
    setIsLoadingChart(true);
    setAutoScale(!autoScale);
    mapRef.current.eachLayer((layer) => {
      if (layer instanceof L.Marker && layer.options.isDotChart) {
        mapRef.current.removeLayer(layer);
      }
    });
  };

  const handleChartHover = (e) => {
    //console.log("e ",e);
    if (!e.activePayload) return;
    const hoveredData = e.activePayload[0].payload;
    //console.log("hoveredData ",hoveredData);

    
    // Remove previous red marker if it exists
    if (redMarkerRef.current) {
      mapRef.current.removeLayer(redMarkerRef.current);
    }

    // Create a custom icon with a larger size
    const bigIcon = L.icon({
      iconUrl:  iconDotChartGray_cs.options.iconUrl, // Reuse existing icon URL
      iconSize: [100, 100], // Set size (width, height)
      iconAnchor: [20, 20], // Center the icon correctly
    });

    // Create new red marker at the hovered point
    const marker = L.marker([hoveredData.latitude, hoveredData.longitude], {
      icon: bigIcon,
      isDotChartBig: true,
    }).addTo(mapRef.current);

    // Save the new marker reference
    redMarkerRef.current = marker;
  };

  const handleChartLeave = () => {
    mapRef.current.eachLayer((layer) => {
      if (layer instanceof L.Marker && layer.options.isDotChartBig) {
        mapRef.current.removeLayer(layer);
      }
    });
  };

  return (
    <div
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        width: "calc(100% - 21rem)",
        backgroundColor: "#fff",
        boxShadow: "0 -2px 5px rgba(0,0,0,0.1)",
        padding: "10px",
        zIndex: 9999,
      }}
    >
      <FormControlLabel
        control={<Switch checked={autoScale} onChange={handleAutoScale} />}
        label="Auto Scale"
      />

      <ResponsiveContainer width="100%" height={150}>
        <AreaChart
          data={formattedData}
          margin={{ top: 10, right: 0, left: -60, bottom: 10 }}
          onMouseMove={handleChartHover}
          onMouseLeave={handleChartLeave}
        >
          <XAxis
            dataKey="formattedTime"
            tick={{ fontSize: 12 }}
            interval={Math.ceil(formattedData.length / 5)}
            tickFormatter={(value, index) => (index === 0 ? "" : value)}
          />
          <YAxis
            domain={[minBoundaryValue, maxBoundaryValue]}
            tick={false}
            axisLine={false}
          />

          {/* Always visible boundaries */}
          <ReferenceLine
            y={nodeForChart?.canUpperBound}
            stroke="black"
            strokeWidth={2}
            strokeDasharray="3 3"
            label={{
              position: "insideTopLeft",
              value: `${nodeForChart?.canUpperBound} ${nodeForChart?.unit}`,
              fill: "black",
              fontSize: 12,
            }}
          />
          <ReferenceLine
            y={nodeForChart?.canLowerBound}
            stroke="black"
            strokeWidth={2}
            strokeDasharray="3 3"
            label={{
              position: "insideTopLeft",
              value: `${nodeForChart?.canLowerBound} ${nodeForChart?.unit}`,
              fill: "black",
              fontSize: 12,
            }}
          />

          <Tooltip />
          {autoScale && (
            <>
              <ReferenceLine
                y={maxBoundaryValue}
                stroke="gray"
                strokeDasharray="3 3"
                label={{
                  position: "insideTopLeft",
                  value: `${maxBoundaryValue} ${nodeForChart?.unit}`,
                  fill: "black",
                  fontSize: 12,
                }}
              />
              <ReferenceLine
                y={minBoundaryValue}
                stroke="gray"
                strokeDasharray="3 3"
                label={{
                  position: "insideTopLeft",
                  value: `${minBoundaryValue} ${nodeForChart?.unit}`,
                  fill: "black",
                  fontSize: 12,
                }}
              />
            </>
          )}
          <Area
            type="monotone"
            dataKey="content"
            stroke="#8884d8"
            fill="#8c8cf0"
            fillOpacity={0.3}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ChartAnalyticMap;
